<template>
  <div class="sales-history-view">
    <ftu-block
      dismiss-key="shop"
      icon="search"
      title="Sales History"
      description="Our database contains 100M+ sales across millions of cards. Start searching and find out today's Card Ladder value for any card. The sales you see with green check marks were vetted by our experienced Research Team."
    />

    <align small-gap flex class="header">
      <align small-gap>
        <h1 class="secondary-font">
          Sales
        </h1>
        <external-link href="https://cardladder.zendesk.com/hc/en-us/articles/23672925718935-How-to-Search-Sales-History" />
      </align>

      <align v-show="!image" small-gap>
        <saved-filters
          type="sales"
          :query="q"
          :filters="filters"
          :is-filtering="isFiltering"
          @select="applySavedFilter"
        />

        <icon-button
          v-show="hasActiveProfileFiltering"
          icon="menu_book"
          class="pop-data-button"
          tooltip="View full pricing breakdown by grade"
          @click="showPopData"
        />
      </align>
    </align>

    <align flex collapse-end direction="flex-start">
      <shared-filters
        show-recent-searches
        placeholder="Search listing titles"
        search-icon="tag"
        :sales-platforms="platformOptions"
        :filters.sync="filters"
        :query.sync="q"
        :properties="defaultFilters"
        :hide-filters="Boolean(image)"
        @clear="clearFilters"
        @searchIconClick="certSearchOpen = true"
      />

      <align>
        <align small-gap>
          <icon-button
            icon="list"
            :class="{ success: salesHistoryViewType === 'list' }"
            @click="salesHistoryViewType = 'list'"
          />

          <icon-button
            icon="view_module"
            :class="{ success: salesHistoryViewType === 'grid' }"
            @click="salesHistoryViewType = 'grid'"
          />
        </align>

        <direction-sort-options
          :sort.sync="sort"
          :direction.sync="direction"
          :options="sortOptions"
        />
      </align>
    </align>

    <filter-results
      :is-filtering="isFiltering"
      :filters="filters"
      :results="numResults && numResults < 10000 ? numResults : null"
      :translation-map="filterResultsTranslationMap"
      :population="population"
      @clear="clearFilters"
    />

    <div v-if="showEstimateSlot" class="estimate">
      <align small-gap>
        <value-logo />

        <div v-if="showEstimate" class="value">
          {{ $filters.currency(estimateData.estimatedValue) }}
        </div>

        <div v-else class="value value-placeholder" />

        <icon
          v-if="showEstimate"
          v-tooltip="{ content: estimateDescription, delay: 100, placement: 'bottom' }"
          name="help_outline"
        />
      </align>
    </div>

    <div class="results">
      <empty-state
        v-if="showEmptyState"
        icon="search_off"
        title="There are no results for your query"
        description="Try searching for something else"
      />

      <template v-else>
        <div v-if="salesHistoryViewType === 'list'" class="list">
          <sales-list-item
            v-for="(item, i) in sales"
            :key="i"
            :item="item"
            as="router-link"
            :to="{ query: { ...$route.query, saleId: item.id } }"
            :href="item.url"
          >
            <align small-gap>
              <verified-icon v-if="item.cardId || item.memberVerified" />

              <icon-button
                v-if="isStaff"
                tooltip="Delete sale"
                icon="delete_outline"
                @click.stop.prevent="deleteSale(item)"
              />
            </align>
          </sales-list-item>
        </div>

        <div v-else class="grid">
          <sales-list-card
            v-for="(item, i) in sales"
            :key="i"
            :item="item"
            as="router-link"
            :to="{ query: { ...$route.query, saleId: item.id } }"
            :href="item.url"
            :verified="Boolean(item.cardId || item.memberVerified)"
          />
        </div>

        <infinite-scroll
          :loading="loading"
          :has-more-data="hasMoreData"
          @load="loadMore"
        />
      </template>
    </div>

    <modal
      title="Search Sales by Cert #"
      :open="certSearchOpen"
      @close="certSearchOpen = false"
    >
      <form @submit.prevent="searchCert">
        <text-input
          v-model="certInput"
          label="Cert #"
          placeholder="63444200"
        />

        <select-input
          v-model="graderInput"
          label="Grader"
          :options="graderOptions"
        />

        <div>
          <checkbox
            v-model="certShowGradeOnly"
            label="Show sales from this grade only"
          />
        </div>

        <btn type="submit" :disabled="!canSubmitCertSearch">
          Submit
        </btn>
      </form>
    </modal>

    <scroll-top />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import debounce from 'lodash.debounce';
import clone from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import firebase from 'firebase/compat/app';
import * as Sentry from '@sentry/vue';

import {
  saleService,
  searchService,
  imageService,
} from '@/services';

import {
  SALES_SORT_OPTIONS,
  DEFAULT_SALES_FILTERS,
  SELLING_PLATFORM_OPTIONS,
  GRADING_COMPANY_UPLOAD_OPTIONS,
} from '@cardladder/shared/constants';

import { getFiltersUrlParams, getFiltersObject } from '@cardladder/shared/utils/ladder.util';
import {
  getCertLookupValidationError,
  isCertLookupInputValid,
  normalizeCertLookupInput,
} from '@cardladder/shared/utils/certLookup.util';
import { getHttpStatus, isSearchAuthError, isSessionExpiredError } from '@/utils/searchAuth.util';

import DirectionSortOptions from '@cardladder/shared/components/DirectionSortOptions';
import FilterResults from '@/components/FilterResults';
import SalesListItem from '@cardladder/shared/components/SalesListItem';
import SalesListCard from '@cardladder/shared/components/SalesListCard';
import SharedFilters from '@/components/SharedFilters';
import VerifiedIcon from '@cardladder/shared/components/VerifiedIcon';
import ValueLogo from '@cardladder/shared/components/ValueLogo';

const GRADING_COMPANY_MAP = {
  psa: 'psa',
  bgs: 'beckett',
  bvg: 'beckett',
  beckett: 'beckett',
  sgc: 'sgc',
  cgc: 'cgc',
  csg: 'cgc',
};

export default {
  components: {
    DirectionSortOptions,
    FilterResults,
    SalesListItem,
    SalesListCard,
    SharedFilters,
    VerifiedIcon,
    ValueLogo,
  },

  data() {
    return {
      limit: 20,
      sales: [],
      numResults: 0,
      loading: false,
      loaded: false,
      hasMoreData: false,
      page: 1,
      sort: 'date',
      direction: 'desc',
      q: '',
      filters: clone(DEFAULT_SALES_FILTERS),
      image: '',
      request: null,
      certSearchOpen: false,
      certInput: '',
      graderInput: 'psa',
      certShowGradeOnly: true,
      estimateData: null,
      estimateLoading: false,
      estimateGeneration: 0,
      graderOptions: GRADING_COMPANY_UPLOAD_OPTIONS,
      sortOptions: SALES_SORT_OPTIONS,
      defaultFilters: DEFAULT_SALES_FILTERS,
      platformOptions: SELLING_PLATFORM_OPTIONS.filter(({ sales }) => sales === true),
      filterResultsTranslationMap: {},
    };
  },

  created() {
    this.updateQuery = debounce(this.updateQuery, 1000);
    this.checkSaleJson();
    this.checkCertQuery();
  },

  mounted() {
    this.$bus.$on('search-card-sales', this.handleCardSales);
  },

  beforeDestroy() {
    this.$bus.$off('search-card-sales', this.handleCardSales);
  },

  watch: {
    page(value) {
      if (value > 1) {
        this.search();
      }
    },

    sort(value) {
      this.updateUrl({ ...this.$route.query, sort: value });
    },

    direction(value) {
      this.updateUrl({ ...this.$route.query, direction: value });
    },

    q(value) {
      this.updateQuery(value);
    },

    filters(value, oldValue) {
      if (isEqual(value, this.defaultFilters)) {
        this.clearFilters();
      } else if (!isEqual(value, oldValue)) {
        const urlParamFilters = getFiltersUrlParams(value, true);

        const query = { ...this.$route.query };

        if (urlParamFilters) {
          query.filters = urlParamFilters;
        }

        this.updateUrl(query);
      }
    },

    '$route.query': {
      immediate: true,
      handler(queryParams, oldParams) {
        const newCopy = { ...queryParams };
        const oldCopy = { ...oldParams };

        delete newCopy.saleId;
        delete oldCopy.saleId;

        if (!isEqual(newCopy, oldCopy) || !oldParams) {
          this.reset(queryParams);
        }
      },
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    ...mapGetters({
      isStaff: 'isStaff',
    }),

    salesHistoryViewType: {
      get() {
        return this.$store.state.salesHistoryViewType;
      },

      set(value) {
        this.$store.commit('SET_SALES_HISTORY_VIEW_TYPE', value);
      },
    },

     showEmptyState() {
      return !this.loading && this.loaded && this.sales.length === 0;
    },

    isFiltering() {
      return Boolean(this.q?.trim().length > 0) || !isEqual(this.filters, this.defaultFilters);
    },

    showEstimate() {
      return this.estimateData && this.estimateData.estimatedValue && this.estimateData.lastSaleDate && this.loaded && (!this.user || !this.user.hideSalesHistoryValue);
    },

    isEstimateEligible() {
      return Boolean(
        this.filters?.profileId
        && this.filters?.grade
        && this.filters?.grader
        && !this.filters?.hasQualifier
        && !this.filters?.hasAutographGrade,
      );
    },

    showEstimateSlot() {
      return this.isEstimateEligible
        && this.loaded
        && this.sales.length > 0
        && (!this.user || !this.user.hideSalesHistoryValue)
        && (this.estimateLoading || this.showEstimate);
    },

    hasActiveProfileFiltering() {
      return this.filters?.profileId;
    },

    canSubmitCertSearch() {
      return isCertLookupInputValid(this.certInput, this.graderInput);
    },

    estimateDescription() {
      let description = null;

      if (this.estimateData && this.estimateData.lastSaleDate && this.estimateData.indexPercentChange && this.estimateData.index) {
        description = `The CL Value is calculated by looking at the last sale of this card on ${this.$filters.date(this.estimateData.lastSaleDate)}. `;
        description += `This card matches the ${this.estimateData.index} index which has gone ${this.estimateData.indexPercentChange < 0 ? 'down' : 'up'} by ${this.$filters.percentage(this.estimateData.indexPercentChange)} since the sale date. `;
        description += 'If we multiply the sale price by this percentage we arrive at our value.';
      }

      return description;
    },

    population() {
      return this.estimateData?.population;
    },
  },

  methods: {
    reset(queryParams) {
      this.page = 1;
      this.hasMoreData = true;
      this.loaded = false;
      this.estimateData = null;
      this.estimateLoading = false;
      this.estimateGeneration += 1;

      const params = {
        sort: queryParams.sort || 'date',
        direction: queryParams.direction || 'desc',
        q: queryParams.q || '',
        image: queryParams.image || '',
        filters: { ...clone(DEFAULT_SALES_FILTERS), ...getFiltersObject(queryParams.filters) },
      };

      this.sort = params.sort;
      this.direction = params.direction;
      this.q = params.q;
      this.image = params.image;
      this.filters = params.filters;

      if (this.image) {
        this.searchImage();
      } else {
        this.search(true);
      }
    },

    loadMore() {
      this.page += 1;
    },

    async searchImage() {
      try {
        this.loading = true;

        const queryRecords = [];
        const {
          isHorizontal,
          base64Url: verticalBase64Url,
        } = await imageService.getImageFromUrl(this.image.replace('@3x', '@1x').replace('@2x', '@1x'));

        if (isHorizontal) {
          queryRecords.push({ '_base64': verticalBase64Url });
          queryRecords.push({ '_url': this.image });
        } else {
          queryRecords.push({ '_url': this.image });
        }

        const data = await searchService.searchImage({
          source: 'salesarchive',
          queryRecords,
          limit: 50,
          sort: this.sort,
          direction: this.direction,
        });

        this.sales = data.hits;
        this.hasMoreData = false;
      } catch (e) {
        if (getHttpStatus(e) === 413) {
          this.$toasted.error('File size too large');
        } else if (getHttpStatus(e) === 429) {
          this.$toasted.error('Too many searches. Please wait and try again.');
        } else if (isSessionExpiredError(e)) {
          this.$toasted.error('Your session expired. Please sign in again.');
        } else if (isSearchAuthError(e)) {
          this.$toasted.error('Your session expired. Please refresh and try again.');
        } else {
          this.$toasted.error('Error loading sales');
        }

        Sentry.captureException(e);
      }

      this.loaded = true;
      this.loading = false;
    },

    async searchCert() {
      const validationError = getCertLookupValidationError(this.certInput, this.graderInput);

      if (validationError) {
        this.$toasted.error(validationError);
        return;
      }

      const normalizedInput = normalizeCertLookupInput(this.certInput, this.graderInput);

      try {
        this.certSearchOpen = false;
        this.loading = true;

        const { data } = await firebase.functions().httpsCallable('httpcertlookup')({
          cert: normalizedInput.cert,
          grader: normalizedInput.grader,
          includeProfile: !this.certShowGradeOnly,
        });

        const profileId = data.profileId;
        const canonicalProfileId = data.profile && data.profile.canonicalProfileId;

        if (canonicalProfileId || profileId) {
          const filters = {
            profileId: canonicalProfileId || profileId,
          };

          this.filterResultsTranslationMap[canonicalProfileId || profileId] = data.label;

          if (this.certShowGradeOnly) {
            filters.grader = data.grader;
            filters.grade = data.grade;
            filters.qualifierType = data.qualifierType;
            filters.autographGrade = data.autographGrade;
          }

          const urlParamFilters = getFiltersUrlParams({
            ...clone(this.defaultFilters),
            ...filters,
          }, true);

          this.updateUrl({ ...this.$route.query, filters: urlParamFilters, q: '' });
        } else {
          this.$toasted.error('No information with this cert');
        }
      } catch (e) {
        if (e?.code === 'functions/invalid-argument') {
          this.$toasted.error(e.message || 'Invalid cert number');
        } else {
          this.$toasted.error('No information with this cert');
        }
      }

      this.loaded = true;
      this.loading = false;
      this.certInput = '';
    },

    async search(reset = false) {
      const estimateGeneration = this.estimateGeneration + 1;
      this.estimateGeneration = estimateGeneration;

      try {
        this.loading = true;
        this.estimateData = null;
        this.estimateLoading = false;

        const request = {
          index: 'salesarchive',
          limit: this.limit,
          page: this.page,
          sort: this.sort,
          direction: this.direction,
          query: this.q,
          filters: this.filters,
          fuzziness: this.user && this.user.enableFuzzinessSearch,
        };

        this.request = request;

        const salesData = await searchService.search(request);

        if (request === this.request) {
          this.numResults = salesData.totalHits;

          if (reset) {
            this.sales = salesData.hits;
          } else {
            this.sales = [...this.sales, ...salesData.hits];
          }

          if (salesData.hits.length < this.limit) {
            this.hasMoreData = false;
          }

          this.loaded = true;
          this.loading = false;

          const shouldLoadEstimate = reset && this.isEstimateEligible;

          if (shouldLoadEstimate && salesData.hits.length > 0) {
            this.estimateLoading = true;
            this.loadEstimate(estimateGeneration);
          }
        }
      } catch (e) {
        if (getHttpStatus(e) === 413) {
          this.$toasted.error('File size too large');
        } else if (getHttpStatus(e) === 429) {
          this.$toasted.error('Too many searches. Please wait and try again.');
        } else if (isSessionExpiredError(e)) {
          this.$toasted.error('Your session expired. Please sign in again.');
        } else if (isSearchAuthError(e)) {
          this.$toasted.error('Your session expired. Please refresh and try again.');
        } else {
          this.$toasted.error('Error loading sales');
        }

        Sentry.captureException(e);
      }

      this.loaded = true;
      this.loading = false;
    },

    async loadEstimate(estimateGeneration) {
      try {
        const filters = {
          profileId: this.filters.profileId,
          grader: this.filters.grader,
          grade: this.filters.grade,
          qualifierType: this.filters.qualifierType,
          autographGrade: this.filters.autographGrade,
        };

        const { data: estimateResponse } = await firebase.functions().httpsCallable('httpcardestimate')(filters);

        if (estimateGeneration !== this.estimateGeneration) {
          return;
        }

        this.estimateData = estimateResponse;
      } catch (e) {
        // swallow
      }

      if (estimateGeneration === this.estimateGeneration) {
        this.estimateLoading = false;
      }
    },

    async deleteSale(item) {
      if (window.confirm('Are you sure you want to delete this sale?')) {
        try {
          await saleService.deleteArchiveSale(item);
          this.$toasted.success('Deleted the sale!');
          this.sales = this.sales.filter(({ itemId: localItemId }) => localItemId !== item.itemId);
        } catch (e) {
          this.$toasted.error('Error deleting the sale');
          Sentry.captureException(e);
        }
      }
    },

    updateQuery(value) {
      const query = { ...this.$route.query };

      if (value) {
        query.q = value;
      } else {
        delete query.q;
      }

      this.updateUrl(query);
    },

    updateUrl(query) {
      if (!isEqual(query, this.$route.query)) {
        if (!query.q) {
          delete query.q;
        }

        if (!query.filters) {
          delete query.filters;
        }

        delete query.query;

        const saveUrlSearchParams = new URLSearchParams(query);

        saveUrlSearchParams.delete('saleId');

        this.$store.commit('SET_SALES_HISTORY_QUERY', saveUrlSearchParams.toString());
        this.$router.push({ query });
      }
    },

    clearFilters() {
      const query = { ...this.$route.query };

      delete query.filters;

      this.updateUrl(query);
    },

    handleCardSales(filters) {
      if (filters.profileId) {
        const [id, description] = filters.profileId.split(':');

        if (description) {
          this.filterResultsTranslationMap[id] = description;
        }

        filters.profileId = id;
      }

      const urlParamFilters = getFiltersUrlParams({
        ...clone(this.defaultFilters),
        ...filters,
      }, true);

      this.updateUrl({ ...this.$route.query, filters: urlParamFilters, q: '' });
      window.scrollTo(0, 0);
    },

    showPopData() {
      this.$router.push(`/profiles/${this.filters.profileId}`);
    },

    checkSaleJson() {
      if (this.$route.query && this.$route.query.saleJson && this.isStaff) {
        const sale = JSON.parse(decodeURIComponent(this.$route.query.saleJson.replace('/o/sales%2F', '/o/sales/').replaceAll('%', '')));

        sale.date = new Date(sale.date);
        sale.image = sale.image.replace('/o/sales/', '/o/sales%2F');

        const query = { ...this.$route.query };
        delete query.saleJson;
        this.$router.replace({ query });

        setTimeout(() => {
          this.$bus.$emit('sales-history-item-edit', sale);
        }, 1000);
      }
    },

    checkCertQuery() {
      if (this.$route.query && this.$route.query.cert && this.$route.query.grader && GRADING_COMPANY_MAP[this.$route.query.grader]) {
        this.certInput = this.$route.query.cert;
        this.graderInput = GRADING_COMPANY_MAP[this.$route.query.grader];
        this.certShowGradeOnly = true;

        const query = { ...this.$route.query };

        delete query.cert;
        delete query.grader;

        this.$router.replace({ query });

        this.searchCert();
      }
    },

    applySavedFilter(savedFilter) {
      const urlParamFilters = getFiltersUrlParams({
        ...clone(this.defaultFilters),
        ...savedFilter.filters,
      }, true);

      this.updateUrl({ ...this.$route.query, filters: urlParamFilters, q: savedFilter.query || '' });
    },
  },
};
</script>

<style lang="scss" scoped>
.sales-history-view {
  @include container;
  min-height: 100%;

  label {
    font-weight: bold;
    font-size: 18px;
    margin-bottom: $gp * .5;
    display: block;
  }
}

.header {
  margin-bottom: $gp;

  h1 {
    margin-bottom: 0;
  }
}

.results {
  position: relative;
  flex: 1;
  margin-top: $gp;
}

.estimate {
  margin: $gp 0 $gp * .5;
  min-height: 28px;
  --value-logo-size: 16px;
  --icon-size: 16px;

  .value {
    font-size: 20px;
    font-weight: 700;
    font-family: $font-family-secondary;
  }

  .value-placeholder {
    width: 88px;
    height: 20px;
    border-radius: 4px;
    background-color: $color-gray-5;
    opacity: 0.35;
  }
}

.list {
  @media($medium-down) {
    margin: 0 (-$gp);
  }
}

.grid {
  @include grid-panel;
}

.pop-data-button {
  --theme-icon-button-color: #{$color-green-1};
}
</style>
